/**
 * Dates et heures — toujours affichées dans le fuseau de l'ÉVÉNEMENT.
 *
 * Un invité qui consulte le site depuis Douala doit lire « 18:00 », l'heure à
 * laquelle les portes ouvrent à Berlin, pas l'heure correspondante chez lui.
 * Le fuseau vient de `events.timezone`, jamais du navigateur.
 */

export function formatDate(iso: string, locale: string, timeZone: string): string {
  return new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone,
  }).format(new Date(iso))
}

export function formatTime(iso: string, locale: string, timeZone: string): string {
  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  }).format(new Date(iso))
}

export function formatDateTime(iso: string, locale: string, timeZone: string): string {
  return `${formatDate(iso, locale, timeZone)} · ${formatTime(iso, locale, timeZone)}`
}

/** Format compact requis par les liens « Ajouter au calendrier » : 20261114T170000Z. */
export function toCalendarStamp(iso: string): string {
  return new Date(iso).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')
}

export function googleCalendarUrl(params: {
  title: string
  details: string
  location: string
  startsAt: string
  endsAt: string | null
}): string {
  const end = params.endsAt ?? new Date(new Date(params.startsAt).getTime() + 6 * 3600_000).toISOString()
  const url = new URL('https://calendar.google.com/calendar/render')
  url.searchParams.set('action', 'TEMPLATE')
  url.searchParams.set('text', params.title)
  url.searchParams.set('details', params.details)
  url.searchParams.set('location', params.location)
  url.searchParams.set('dates', `${toCalendarStamp(params.startsAt)}/${toCalendarStamp(end)}`)
  return url.toString()
}

/**
 * ISO → valeur d'un `<input type="datetime-local">`, lue dans le fuseau de
 * l'ÉDITION — et retour.
 *
 * Le champ natif ne connaît que l'heure du navigateur : saisir « 22:00 »
 * depuis un téléphone resté à l'heure de Douala programmait l'ouverture d'un
 * vote une heure trop tôt à Berlin. On convertit donc nous-mêmes, avec le
 * fuseau de `events.timezone`.
 */
export function toZonedInput(iso: string | null, timeZone: string): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const parts = zonedParts(date, timeZone)
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}`
}

export function fromZonedInput(value: string, timeZone: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value)
  if (!match) return null
  const [, year, month, day, hour, minute] = match.map(Number) as number[]
  const wanted = Date.UTC(year!, month! - 1, day!, hour!, minute!)
  // Deux passes : la première estime le décalage, la seconde le corrige
  // quand l'heure saisie tombe de l'autre côté d'un changement d'heure.
  let guess = wanted
  for (let pass = 0; pass < 2; pass += 1) {
    const parts = zonedParts(new Date(guess), timeZone)
    const seen = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute)
    guess += wanted - seen
  }
  return new Date(guess).toISOString()
}

function zonedParts(date: Date, timeZone: string) {
  const values: Record<string, number> = {}
  for (const part of new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(date)) {
    if (part.type !== 'literal') values[part.type] = Number(part.value)
  }
  return {
    year: values.year ?? 1970,
    month: values.month ?? 1,
    day: values.day ?? 1,
    hour: (values.hour ?? 0) % 24,
    minute: values.minute ?? 0,
  }
}
